import {
  TOrganizationDetail,
  TOrganizationLog,
  TProjectDetail
} from "admin-app/models";
import { FeatureTypes } from "entity-app/constants";
import {
  AUDIT_FEATURE_EVENTS_BY_WHERE,
  FEATURE_INSTANCE_HISTORY_BY_WHERE,
  FEATURE_LIST_BY_FEATURE_TYPE_ID,
  ORGANIZATION_DETAIL,
  ORGANIZATION_LIST,
  PROJECT_USERS_BY_PROJECT,
  PROJECT_VENDORS_BY_PROJECT,
  PROJECTS_OF_SUBSCRIPTION
} from "admin-app/services/ciq-queries";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useQuerySubscription } from "hooks/common";
import { useEffect, useMemo, useState } from "react";
import { getGQL } from "utils/utils";

type TSelectOption = {
  label: string;
  value: string | number;
};

type TUserBasic = {
  email: string | null;
  first_name: string | null;
  last_name: string | null;
};

type TAuditFeature = {
  name: string | null;
};

export type TEntityFeatureAudit = {
  feature_instance_id: string;
  field: string | null;
  new_number: number | null;
  new_string: string | null;
  new_date: string | null;
  old_date: string | null;
  old_number: number | null;
  old_string: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at?: string | null;
  event_log?: {
    event?: {
      name?: string | null;
    } | null;
  } | null;
  user_created?: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  feature: TAuditFeature | null;
};

type THistoryActionData = {
  new?: Record<string, unknown> | null;
  old?: Record<string, unknown> | null;
  field?: {
    key?: string;
    display_name?: string;
    value?: {
      new?: { display_data?: string | null };
      old?: { display_data?: string | null };
    };
  };
  subject?: {
    user?: {
      id?: string;
      name?: string;
      email?: string;
    };
  };
  user_id?: string;
  created_at?: string;
  created_by?: string;
  project_id?: string;
  table_name?: string;
  feature_instance_id?: string;
  action_type_id?: number;
  feature_type_id?: number;
};

export type TEntityFeatureHistory = {
  id: string;
  action_data?: THistoryActionData | null;
  action_description_html: string | null;
  action_description: string | null;
  action_type: number | null;
  created_at: string | null;
  updated_at?: string | null;
  feature_instance_id: string;
  feature_type_id: number | null;
  comment: string | null;
  feature_instance_history_created_by_user?: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
};

export type TEntityFeatureInstance = {
  id: string;
  feature_type_id: number;
  auto_inc_value: number | null;
  title: string | null;
  created_at: string | null;
  created_by_user: TUserBasic | null;
  updated_at: string | null;
  updated_by_user: TUserBasic | null;
  feature_instance_last_updated?: Array<{
    feature_instance_id: string;
    last_updated_at: string | null;
  }> | null;
};

export type TEntityAuditRow = TEntityFeatureInstance & {
  entity_type_label: string;
  audits?: TEntityFeatureAudit[];
  history?: TEntityFeatureHistory[];
};

export type TEntityAuditResponse = {
  featureInstances: TEntityAuditRow[];
  featureAudits: TEntityFeatureAudit[];
  featureHistory: TEntityFeatureHistory[];
};

export type TEntityAuditTabKey =
  | "submittals"
  | "materials"
  | "project_users"
  | "project_companies";

type TUserNameFields = {
  email: string | null;
  first_name: string | null;
  last_name: string | null;
};

export type TProjectUserRow = {
  id: string;
  user_id: string;
  project_id: string;
  created_at: string;
  updated_at: string | null;
  external: boolean;
  user: TUserNameFields & { id: string };
  project_role: { id: number; name: string } | null;
  status: { id: number; name: string };
  created_by_user: TUserNameFields | null;
  updated_by_user: TUserNameFields | null;
};

export type TProjectUserMasterRow = {
  groupKey: string;
  groupTitle: string;
  email: string;
  roleName: string;
  statusName: string;
  external: boolean;
  createdBy: string;
  createdAt: string | null;
  updatedBy: string;
  updatedAt: string | null;
  projectUser: TProjectUserRow;
};

export type TProjectVendorRow = {
  id: number;
  project_id: string;
  vendor_id: string;
  vendor_name: string | null;
  type_id: number | null;
  status: number | null;
  created_at: string | null;
  updated_at: string | null;
  created_by_user: TUserNameFields | null;
  updated_by_user: TUserNameFields | null;
};

export type TProjectVendorMasterRow = {
  groupKey: string;
  groupTitle: string;
  roleName: string;
  statusName: string;
  createdBy: string;
  createdAt: string | null;
  updatedBy: string;
  updatedAt: string | null;
  projectVendor: TProjectVendorRow;
};

const ENTITY_AUDIT_TAB_OPTIONS: {
  label: string;
  value: TEntityAuditTabKey;
}[] = [
  { label: "Submittals", value: "submittals" },
  { label: "Materials", value: "materials" },
  { label: "Project Users", value: "project_users" },
  { label: "Project Companies", value: "project_companies" }
];

const sortOptionsByLabel = (options: TSelectOption[]) => {
  return [...options].sort((firstOption, secondOption) =>
    `${firstOption.label}`.localeCompare(`${secondOption.label}`)
  );
};

export default function useEntityAuditData() {
  const [organizationId, setOrganizationId] = useState<string>();
  const [subscriptionId, setSubscriptionId] = useState<string>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const [entityTab, setEntityTab] = useState<TEntityAuditTabKey | undefined>();

  const normalizedFeatureTypeId = useMemo(() => {
    if (entityTab === "submittals") {
      return FeatureTypes.SUBMITTALS;
    }
    if (entityTab === "materials") {
      return FeatureTypes.MATERIALS;
    }
    return undefined;
  }, [entityTab]);

  const { query: qOrganizationList, sub: sOrganizationList } =
    getGQL(ORGANIZATION_LIST);
  const { data: organizationData, loading: organizationsLoading } =
    useQuerySubscription<{
      organization: Array<TOrganizationLog>;
    }>(qOrganizationList, sOrganizationList, {});

  const { query: qOrganizationDetail, sub: sOrganizationDetail } =
    getGQL(ORGANIZATION_DETAIL);
  const { data: organizationDetailData, loading: subscriptionsLoading } =
    useQuerySubscription<{
      organization_by_pk: TOrganizationDetail;
    }>(qOrganizationDetail, sOrganizationDetail, {
      variables: { id: organizationId },
      skip: !organizationId
    });

  const { query: qProjects, sub: sProjects } = getGQL(PROJECTS_OF_SUBSCRIPTION);
  const { data: projectsData, loading: projectsLoading } =
    useQuerySubscription<{
      project: Array<TProjectDetail>;
    }>(qProjects, sProjects, {
      variables: { subscription_id: subscriptionId },
      skip: !subscriptionId
    });

  useEffect(() => {
    setSubscriptionId(undefined);
    setProjectId(undefined);
    setEntityTab(undefined);
  }, [organizationId]);

  useEffect(() => {
    setProjectId(undefined);
    setEntityTab(undefined);
  }, [subscriptionId]);

  useEffect(() => {
    setEntityTab(undefined);
  }, [projectId]);

  const featureWhere = useMemo(
    () => ({
      _and: [
        { project_id: { _eq: projectId } },
        {
          feature_type_id: { _eq: normalizedFeatureTypeId }
        },
        { deleted: { _eq: false } }
      ]
    }),
    [normalizedFeatureTypeId, projectId]
  );

  const { data: entityData, loading: entityListLoading } = useCIQQuery<{
    feature_instance: TEntityFeatureInstance[];
  }>(FEATURE_LIST_BY_FEATURE_TYPE_ID, {
    variables: {
      where: featureWhere
    },
    skip: !projectId || normalizedFeatureTypeId === undefined
  });

  const entityList = useMemo<TEntityAuditRow[]>(() => {
    const featureInstances = entityData?.feature_instance || [];
    const filteredList: TEntityAuditRow[] = [];

    for (let index = 0; index < featureInstances.length; index += 1) {
      const item = featureInstances[index];
      const autoIncValue = item?.auto_inc_value;

      if (
        autoIncValue !== null &&
        autoIncValue !== undefined &&
        `${autoIncValue}`.trim() !== ""
      ) {
        const entityTypeLabel =
          entityTab === "submittals"
            ? "Submittals"
            : entityTab === "materials"
              ? "Materials"
              : "Unknown";
        const derivedUpdatedAt =
          item?.feature_instance_last_updated?.[0]?.last_updated_at ||
          item?.updated_at ||
          null;

        filteredList.push({
          ...item,
          updated_at: derivedUpdatedAt,
          entity_type_label: entityTypeLabel
        });
      }
    }

    return filteredList;
  }, [entityData?.feature_instance, entityTab]);

  const featureInstanceIds = useMemo(
    () => entityList.map((featureInstance) => featureInstance.id),
    [entityList]
  );

  const auditWhere = useMemo(
    () => ({
      _and: [
        { project_id: { _eq: projectId } },
        {
          feature_id: { _eq: normalizedFeatureTypeId }
        },
        { feature_instance_id: { _in: featureInstanceIds } }
      ]
    }),
    [normalizedFeatureTypeId, featureInstanceIds, projectId]
  );

  const { data: auditData } = useCIQQuery<{
    audit_project_feature_events: TEntityFeatureAudit[];
  }>(AUDIT_FEATURE_EVENTS_BY_WHERE, {
    variables: {
      where: auditWhere
    },
    skip:
      !projectId ||
      normalizedFeatureTypeId === undefined ||
      featureInstanceIds.length === 0
  });

  const historyWhere = useMemo(
    () => ({
      _and: [
        {
          feature_type_id: { _eq: normalizedFeatureTypeId }
        },
        { feature_instance_id: { _in: featureInstanceIds } }
      ]
    }),
    [normalizedFeatureTypeId, featureInstanceIds]
  );

  const { data: historyData } = useCIQQuery<{
    feature_instance_history: TEntityFeatureHistory[];
  }>(FEATURE_INSTANCE_HISTORY_BY_WHERE, {
    variables: {
      where: historyWhere
    },
    skip:
      !projectId ||
      normalizedFeatureTypeId === undefined ||
      featureInstanceIds.length === 0
  });

  const entityAuditResponse = useMemo<TEntityAuditResponse>(() => {
    const featureInstances = entityList;
    const featureAudits = auditData?.audit_project_feature_events || [];
    const featureHistory = historyData?.feature_instance_history || [];
    const validFeatureInstanceIds = new Set(
      featureInstances.map((featureInstance) => featureInstance.id)
    );
    const filteredFeatureAudits: TEntityFeatureAudit[] = [];
    const filteredFeatureHistory: TEntityFeatureHistory[] = [];
    const auditsByFeatureInstanceId: Record<string, TEntityFeatureAudit[]> = {};
    const historyByFeatureInstanceId: Record<string, TEntityFeatureHistory[]> =
      {};

    for (let index = 0; index < featureAudits.length; index += 1) {
      const auditItem = featureAudits[index];
      const featureInstanceId = auditItem.feature_instance_id;
      if (
        !featureInstanceId ||
        !validFeatureInstanceIds.has(featureInstanceId)
      ) {
        continue;
      }

      if (!auditsByFeatureInstanceId[featureInstanceId]) {
        auditsByFeatureInstanceId[featureInstanceId] = [];
      }
      auditsByFeatureInstanceId[featureInstanceId].push(auditItem);
      filteredFeatureAudits.push(auditItem);
    }

    for (let index = 0; index < featureHistory.length; index += 1) {
      const historyItem = featureHistory[index];
      const featureInstanceId = historyItem.feature_instance_id;
      if (
        !featureInstanceId ||
        !validFeatureInstanceIds.has(featureInstanceId)
      ) {
        continue;
      }

      if (!historyByFeatureInstanceId[featureInstanceId]) {
        historyByFeatureInstanceId[featureInstanceId] = [];
      }
      historyByFeatureInstanceId[featureInstanceId].push(historyItem);
      filteredFeatureHistory.push(historyItem);
    }

    const nestedFeatureInstances = featureInstances.map((featureInstance) => ({
      ...featureInstance,
      audits: auditsByFeatureInstanceId[featureInstance.id] || [],
      history: historyByFeatureInstanceId[featureInstance.id] || []
    }));

    return {
      featureInstances: nestedFeatureInstances,
      featureAudits: filteredFeatureAudits,
      featureHistory: filteredFeatureHistory
    };
  }, [
    auditData?.audit_project_feature_events,
    entityList,
    historyData?.feature_instance_history
  ]);

  const organizationOptions = useMemo(
    () =>
      sortOptionsByLabel(
        (organizationData?.organization || []).map((organization) => ({
          label: organization.name,
          value: organization.id
        }))
      ),
    [organizationData?.organization]
  );

  const subscriptionOptions = useMemo(
    () =>
      sortOptionsByLabel(
        (
          organizationDetailData?.organization_by_pk
            ?.organization_subscriptions || []
        ).map((item) => ({
          label: item.subscription.name,
          value: item.subscription.id
        }))
      ),
    [organizationDetailData?.organization_by_pk?.organization_subscriptions]
  );

  const projectOptions = useMemo(
    () =>
      sortOptionsByLabel(
        (projectsData?.project || []).map((project) => ({
          label: project.name,
          value: project.id
        }))
      ),
    [projectsData?.project]
  );

  const entityOptions = useMemo(
    () => sortOptionsByLabel(ENTITY_AUDIT_TAB_OPTIONS),
    []
  );

  const projectUsersWhere = useMemo(
    () => ({
      project_id: { _eq: projectId }
    }),
    [projectId]
  );

  const { data: projectUsersData, loading: projectUsersLoading } = useCIQQuery<{
    project_users: TProjectUserRow[];
  }>(PROJECT_USERS_BY_PROJECT, {
    variables: {
      where: projectUsersWhere
    },
    skip: !projectId || entityTab !== "project_users"
  });

  const projectUsers = useMemo(
    () => projectUsersData?.project_users || [],
    [projectUsersData?.project_users]
  );

  const projectVendorsWhere = useMemo(
    () => ({
      project_id: { _eq: projectId }
    }),
    [projectId]
  );

  const { data: projectVendorsData, loading: projectVendorsLoading } =
    useCIQQuery<{
      project_vendors: TProjectVendorRow[];
    }>(PROJECT_VENDORS_BY_PROJECT, {
      variables: {
        where: projectVendorsWhere
      },
      skip: !projectId || entityTab !== "project_companies"
    });

  const projectVendors = useMemo(
    () => projectVendorsData?.project_vendors || [],
    [projectVendorsData?.project_vendors]
  );

  return {
    organizationId,
    subscriptionId,
    projectId,
    entityTab,
    setOrganizationId,
    setSubscriptionId,
    setProjectId,
    setEntityTab,
    organizationOptions,
    subscriptionOptions,
    projectOptions,
    entityOptions,
    organizationsLoading,
    subscriptionsLoading,
    projectsLoading,
    entityList,
    entityListLoading,
    entityAuditResponse,
    projectUsers,
    projectUsersLoading,
    projectVendors,
    projectVendorsLoading
  };
}
